import { Box, Typography } from '@mui/material';
import { memo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { Sort } from '../../../../models/CommonModels';
import {
  CancelScheduledOperationRequest,
  GetDeviceByIdFn,
  GetDeviceFn,
  Sim,
  SimUpdateRequest,
} from '../../../../models/UserDevicesModels';
import { UserOperationsType } from '../../../../models/UsersModels';

import { RootState } from '../../../../stores/Store';

import Button from '../../../../components/Button/Button';
import Table from '../../../../components/Table/Table';
import useUserDetailsSimsTable from './useUserDetailsSimsTable';

interface UserDetailsSimsTableProps {
  sims: Sim[] | [];
  isLoading: boolean;
  getDeviceById: GetDeviceByIdFn | GetDeviceFn;
  isPerformingAction: boolean;
  resendNotification: (deviceId: string) => void;
  onNewConnectionSuccess: () => void;
  onUpgradeSuccess: () => void;
  onSuspendSimSuccess: () => void;
  onRestoreConnectionSuccess: () => void;
  showTotalSims?: boolean;
  onTerminateConnectionSuccess: () => void;
  updateSim: (req: SimUpdateRequest, errorCallback: () => void) => void;
  cancelScheduledOperation: (req: CancelScheduledOperationRequest) => void;
  assignSim?: () => void;
}

function UserDetailsSimsTable({
  sims,
  isLoading,
  getDeviceById,
  isPerformingAction,
  resendNotification,
  onNewConnectionSuccess,
  onUpgradeSuccess,
  onSuspendSimSuccess,
  onRestoreConnectionSuccess,
  showTotalSims = true,
  onTerminateConnectionSuccess,
  updateSim,
  cancelScheduledOperation,
  assignSim,
}: UserDetailsSimsTableProps) {
  const [t] = useTranslation();
  const [sort, setSort] = useState<Sort>({ order: 'asc', orderBy: 'status' });
  const { userOperations } = useSelector((state: RootState) => state.eSimManagerReducer);
  const { headers, rows, totalSims } = useUserDetailsSimsTable(
    sims,
    sort,
    resendNotification,
    isPerformingAction,
    getDeviceById,
    onNewConnectionSuccess,
    onUpgradeSuccess,
    onSuspendSimSuccess,
    onRestoreConnectionSuccess,
    onTerminateConnectionSuccess,
    updateSim,
    cancelScheduledOperation,
  );
  /**
   * Handle change of the table sort
   * @param s
   */
  const handleOnSort = (s: Sort) => {
    setSort(s);
  };

  return (
    <Box paddingBottom="3rem">
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {showTotalSims ? (
          <Typography variant="body1" paddingBottom="1rem">
            {t('common.table.totalItems', { count: totalSims })}
          </Typography>
        ) : null}
        {userOperations.includes(UserOperationsType.CAN_ASSIGN_SIM_TO_USER) && assignSim ? (
          <Box sx={{ display: 'flex', marginLeft: 'auto', paddingBottom: '1rem' }}>
            <Button onClick={assignSim} label={t('pages.assignSim.title')} />
          </Box>
        ) : null}
      </Box>
      <Table
        headers={headers}
        rows={rows}
        isLoading={isLoading}
        defaultOrderBy={sort.orderBy}
        onSort={handleOnSort}
        emptyStateMessage={t('pages.userDetails.tabs.simsTable.noResults')}
      />
    </Box>
  );
}

export default memo(UserDetailsSimsTable);
